import type { PropsWithChildren, ReactNode } from "react";

interface Feature {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}

interface AuthLayoutProps {
  headline: string;
  subtext: string;
  features: Feature[];
  formEyebrow?: ReactNode;
}

// Curva de deceleracion suave ("expo-out"), la misma para TODAS las animaciones de
// entrada de esta pantalla, para que la coreografia se sienta como un unico gesto
// fluido en vez de varios efectos independientes.
const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

export function AuthLayout({ headline, subtext, features, formEyebrow, children }: PropsWithChildren<AuthLayoutProps>) {
  return (
    <div className="flex min-h-screen bg-ink-950" style={{ viewTransitionName: "auth-shell" }}>
      <style>{`
        @keyframes authFadeUp {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes authOrbFloatA {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(18px, -24px) scale(1.06); }
        }
        @keyframes authOrbFloatB {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50%      { transform: translate(-20px, 16px) scale(0.95); }
        }
        @keyframes authLogoFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-4px) rotate(-3deg); }
        }
        .auth-in {
          opacity: 0;
          animation: authFadeUp 0.9s ${EASE} both;
        }
        @media (prefers-reduced-motion: reduce) {
          .auth-in { animation-duration: 0.01ms !important; }
        }
      `}</style>

      {/* Panel decorativo — identico en login y registro, para que pasar de una pantalla a otra no de un salto */}
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-gradient-to-br from-brand-600 via-brand-700 to-ink-950 p-12 text-white lg:flex">
        <div
          className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-brand-300/20 blur-3xl"
          style={{ animation: "authOrbFloatA 13s ease-in-out infinite" }}
        />
        <div
          className="pointer-events-none absolute bottom-0 left-0 h-72 w-72 rounded-full bg-white/10 blur-3xl"
          style={{ animation: "authOrbFloatB 15s ease-in-out infinite" }}
        />
        <div
          className="pointer-events-none absolute top-1/3 left-1/4 h-48 w-48 rounded-full bg-brand-400/15 blur-3xl"
          style={{ animation: "authOrbFloatA 10s ease-in-out infinite reverse" }}
        />
        <div className="pointer-events-none absolute inset-0 opacity-[0.06] [background-image:radial-gradient(circle,white_1px,transparent_1px)] [background-size:24px_24px]" />

        <div className="auth-in relative z-10 flex items-center gap-2 text-lg font-bold tracking-tight">
          <span
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 shadow-lg shadow-ink-950/30 backdrop-blur"
            style={{ animation: "authLogoFloat 5s ease-in-out infinite" }}
          >
            🎾
          </span>
          PuntoPadel
        </div>

        <div className="relative z-10 flex flex-col gap-6">
          <h1
            className="auth-in max-w-md text-4xl font-extrabold leading-tight tracking-tight"
            style={{ animationDelay: "80ms" }}
          >
            {headline}
          </h1>
          <p className="auth-in max-w-sm text-brand-50/80" style={{ animationDelay: "160ms" }}>
            {subtext}
          </p>

          <div className="mt-4 flex flex-col gap-3 text-sm text-brand-50/90">
            {features.map((feature, i) => (
              <div
                key={feature.label}
                className="auth-in flex items-center gap-3"
                style={{ animationDelay: `${220 + i * 70}ms` }}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/10 transition-colors duration-300 hover:bg-white/20">
                  <feature.icon className="h-4 w-4" />
                </span>
                {feature.label}
              </div>
            ))}
          </div>

          <div
            className="auth-in mt-2 flex w-fit items-center gap-3 rounded-2xl border border-white/10 bg-white/5 px-4 py-3 shadow-xl shadow-ink-950/20 backdrop-blur"
            style={{ animationDelay: "460ms" }}
          >
            <div className="flex -space-x-2">
              <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-brand-700 bg-brand-300 text-[10px] font-bold text-brand-900">
                JR
              </span>
              <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-brand-700 bg-brand-200 text-[10px] font-bold text-brand-900">
                MC
              </span>
              <span className="flex h-7 w-7 items-center justify-center rounded-full border-2 border-brand-700 bg-white text-[10px] font-bold text-brand-900">
                +8
              </span>
            </div>
            <p className="text-xs text-brand-50/80">
              <span className="font-semibold text-white">Decenas de clubes</span> ya gestionan sus pistas con
              PuntoPadel
            </p>
          </div>
        </div>

        <p className="auth-in relative z-10 text-xs text-brand-50/50" style={{ animationDelay: "520ms" }}>
          © {new Date().getFullYear()} ModularBox
        </p>
      </div>

      {/* Formulario */}
      <div className="flex w-full flex-1 items-center justify-center bg-ink-50 px-6 py-12">
        <div className="w-full max-w-sm">
          <div className="auth-in mb-8 lg:hidden">
            <span className="text-lg font-bold text-ink-900">🎾 PuntoPadel</span>
          </div>

          {formEyebrow && (
            <div className="auth-in mb-4" style={{ animationDelay: "40ms" }}>
              {formEyebrow}
            </div>
          )}

          {children}
        </div>
      </div>
    </div>
  );
}
